import {
  Button,
  DateFilter,
  Modal,
  ModalTitle,
} from '@energybox/react-ui-library/dist/components';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  notifyDownload,
  toggleShowHideIcon,
  triggerToReport,
} from '../../../actions/reportworker';
import styles from './DPReportModal.module.css';
import { ReportTemplate, ReportType } from '../../../types/reportworker';
import ConfirmReportDialog from '../../ShowEquipmentPage/ConfirmReportDialog';
import { useSiteTimezone } from '../../../hooks/useSites';
import { useAppLocale } from '../../../hooks/useAppDetails';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  siteId: number;
}

const DPReportModal: React.FC<Props> = ({ isOpen, onClose, siteId }) => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const [dialogOpen, setDialogOpen] = useState(false);
  const locale = useAppLocale();
  const timezone = useSiteTimezone(siteId);
  useEffect(() => {
    if (!isOpen) {
      setStartDate(undefined);
      setEndDate(undefined);
    }
  }, [isOpen]);

  const handleGenerate = () => {
    if (!startDate || !endDate) return;

    const params = {
      reportTemplate: ReportTemplate.RAW_ENERGY_PANEL_DATA,
      siteId,
      reportFromDate: Math.floor(startDate.getTime() / 1000),
      reportToDate: Math.floor(endDate.getTime() / 1000),
      aggregationLevel: '1h',
      reportType: ReportType.CSV,
    };

    dispatch(
      triggerToReport({
        reportType: ReportType.CSV,
        requestBody: params,
      })
    );
    onClose();
    setDialogOpen(true);
    dispatch(toggleShowHideIcon(true));
    dispatch(notifyDownload(true));
  };

  const actions = (
    <>
      <Button variant='text' onClick={onClose}>
        Cancel
      </Button>
      <Button disabled={!startDate || !endDate} onClick={handleGenerate}>
        Generate
      </Button>
    </>
  );

  const value =
    startDate || endDate
      ? {
          fromDate: startDate,
          toDate: endDate,
        }
      : undefined;

  return (
    <>
      {(isOpen && (
        <Modal
          onClose={onClose}
          actions={actions}
          actionsClassName={styles.actionsContainer}
          containerClassName={styles.modal}
        >
          <div className={styles.content}>
            <ModalTitle className={styles.title}>
              Download Panel Energy Data
            </ModalTitle>
            <div className={styles.description}>
              Please select a date range to export your report
            </div>

            <div className={styles.datePickerContainer}>
              <label className={styles.datePickerLabel}>
                Specify date range*
              </label>
              <DateFilter
                value={value}
                setFilter={dateRange => {
                  setStartDate(dateRange.fromDate);
                  setEndDate(dateRange.toDate);
                }}
                locale={locale}
                ianaTimeZoneCode={timezone}
                hideArrowButtons
                displayDatesAsTitle={false}
                customPickerVariant='date'
              />
            </div>
          </div>
        </Modal>
      )) ||
        null}

      <ConfirmReportDialog
        isCreateDashboardDialogOpen={dialogOpen}
        onSubmit={() => setDialogOpen(false)}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default DPReportModal;
